/* eslint-disable max-len */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { TFunction } from 'i18next';
import {
  ROUTER_CATALOG_DETAILS,
  ROUTER_CATALOG_MAINTENANCE_PLAN,
  ROUTER_CATALOG_OTS,
  ROUTER_CATALOG_VEHICLE,
} from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { InfoCircleIcon } from 'assets/icons';
import { DataContainer } from 'components/DataContainer';
import { getTechnicalDocumentationLink } from 'domains/appContext/AppContext.store';
import { useFetchOtsType } from 'domains/catalog/Catalog.requests';
import { getLastSearchVehicleVersionCode, getMaintenancePlan, getOtsType } from 'domains/catalog/Catalog.store';
import { FLUIDS_ID, MaintenancePlanLocal, TechnicalDataIdParam } from 'domains/catalog/Catalog.types';
import { getCatalogSourceUrl } from 'domains/catalog/Catalog.utils';
import { getUserRights, UserRole } from 'domains/user';
import { getOtsTypeColor } from 'pages/CatalogPage/common/CatalogBanner/ots.utils';
import { TabDocumentation } from 'pages/CatalogPage/common/CatalogBanner/TabDocumentation';
import { TabMaintenanceIam } from 'pages/CatalogPage/common/CatalogBanner/TabMaintenance';
import { OTSTabContent } from 'pages/CatalogPage/common/CatalogBanner/TabOTS';
import { TabRepairMethods } from 'pages/CatalogPage/common/CatalogBanner/TabRepairMethods';
import { TabTechnicalData } from 'pages/CatalogPage/common/CatalogBanner/TabTechnicalData';
import Maintenance from 'pages/CatalogPage/DH/Maintenance';
import { VehicleTechnicalDetails as DHVehicleTechnicalDetails } from 'pages/CatalogPage/DH/VehicleTechnicalDetails/VehicleTechnicalDetails';
import { VehicleTechnicalDetails as IAMVehicleTechnicalDetails } from 'pages/CatalogPage/IAM/VehicleTechnicalDetails/VehicleTechnicalDetails';
import { theme } from 'styles';
import { Flex, Icon, LinkTabItem, Text, WithTooltip } from 'UI';
import { ERROR, getCondArrayItem, getData, isQueryBMM, LOADING, NO_DATA } from 'utils';

const catalogTabLabel = (t: TFunction) => (
  <Text type={'lead_text_white'} noWrap>
    {t('catalog.parts.banner.catalog.title', 'Catalog')}
  </Text>
);

type BuildCatalogTabItemsParams = {
  result: VehicleDetail;
  vehicleRouteChildren?: React.ReactNode;
  setCatalogShowMenu: (v: boolean) => void;
  t: TFunction;
  maintenancePlanData: NO_DATA | MaintenancePlanLocal;
  isMaintenancePlanError: boolean;
  isBMM: boolean;
  technicalDocumentationVersionUrl: string;
  userRights: UserRole[] | undefined;
  isIam: boolean;
  versionCode: string | undefined;
  otsType: { data?: string; count?: number } | undefined;
};

const buildCatalogTabItems = ({
  result,
  vehicleRouteChildren,
  setCatalogShowMenu,
  t,
  maintenancePlanData,
  isMaintenancePlanError,
  isBMM,
  technicalDocumentationVersionUrl,
  userRights,
  isIam,
  versionCode,
  otsType,
}: BuildCatalogTabItemsParams): LinkTabItem[] => {
  const vehicleLink = `${getCatalogSourceUrl(result.catalogSource)}/${result.vehicleKey}${ROUTER_CATALOG_VEHICLE}`;
  // Keep `link` as the clean Catalog-tab navigation URL; `routePath` restores L3 params for Quick Access useParams.
  const vehicleRoutePath = `${vehicleLink}/:carPartGroupId?/:categoryId?/:subcategoryId?/:plateId?`;
  const maintenancePlanTitle = t('catalog.parts.banner.maintenance_plan.title', 'Maintenance plan');

  return [
    {
      key: 1,
      label: catalogTabLabel(t),
      labelActive: catalogTabLabel(t),
      component: vehicleRouteChildren ?? null,
      link: vehicleLink,
      routePath: vehicleRoutePath,
      dataCy: 'tab-vehicle',
    },
    ...getCondArrayItem<LinkTabItem>(
      isBMM
        ? false
        : {
            key: 2,
            label: (
              <Text type={'lead_text_white'} noWrap>
                {t('catalog.parts.banner.vehicle_details.title', 'Vehicle details')}
              </Text>
            ),
            component: result.catalogSource === 'IAM' ? <IAMVehicleTechnicalDetails /> : <DHVehicleTechnicalDetails />,
            link: `${getCatalogSourceUrl(result.catalogSource)}/${result.vehicleKey}${ROUTER_CATALOG_DETAILS}`,
            dataCy: 'tab-vehicle-details',
          },
    ),
    ...(TabMaintenanceIam({
      isIam,
      renderVehicleTabContent: <></>,
      setCatalogShowMenu,
      vehicleDetail: result,
      includeVehicleTabContent: false,
    }) as LinkTabItem[]),
    ...getCondArrayItem<LinkTabItem>(
      getData(maintenancePlanData) || isMaintenancePlanError || maintenancePlanData === LOADING
        ? {
            key: 5,
            label: isMaintenancePlanError ? (
              <WithTooltip title={t('common.backend_error', 'Data temporarily unavailable, please try again later.')}>
                <Text type={'lead_text_white'} noWrap>
                  {maintenancePlanTitle}
                </Text>
              </WithTooltip>
            ) : (
              <DataContainer data={maintenancePlanData}>
                <Text type={'lead_text_white'} noWrap>
                  {maintenancePlanTitle}
                </Text>
              </DataContainer>
            ),
            component: <Maintenance />,
            link: `${getCatalogSourceUrl(result.catalogSource)}/${result.vehicleKey}${ROUTER_CATALOG_MAINTENANCE_PLAN}`,
            isDisabled: isMaintenancePlanError,
          }
        : false,
    ),
    ...(TabTechnicalData({
      isIam,
      userRights,
      versionCode,
      renderVehicleTabContent: <></>,
      vehicleDetail: result,
      includeVehicleTabContent: false,
    }) as LinkTabItem[]),
    ...TabRepairMethods({
      isIam,
      userRights,
      versionCode,
      renderVehicleTabContent: <></>,
      vehicleDetail: result,
      includeVehicleTabContent: false,
    }),
    ...TabDocumentation({
      isIam,
      userRights,
      versionCode,
      technicalDocumentationVersionUrl,
    }),
    ...getCondArrayItem<LinkTabItem>(
      otsType?.data && otsType.data !== 'NONE'
        ? {
            key: 8,
            label: (
              <Flex align={'center'}>
                <Icon IconComponent={InfoCircleIcon} color={theme.color.white} size={23} mr={7} />
                <Text type={'lead_text_white'} noWrap>
                  {otsType.count === 1
                    ? t('catalog.parts.banner.ots.title.single', 'Vehicle under OTS.')
                    : t('catalog.parts.banner.ots.title', 'Vehicle under one or several OTS.')}
                </Text>
              </Flex>
            ),
            component: <OTSTabContent />,
            link: `${getCatalogSourceUrl(result.catalogSource)}/${result.vehicleKey}${ROUTER_CATALOG_OTS}`,
            staticWithColor: getOtsTypeColor(otsType.data),
            allowClickOnStatic: true,
            dataCy: 'tab-ots',
          }
        : false,
    ),
  ];
};

export const useCatalogTabItems = ({
  result,
  vehicleRouteChildren,
  setCatalogShowMenu,
}: {
  result: VehicleDetail;
  vehicleRouteChildren?: React.ReactNode;
  setCatalogShowMenu: (v: boolean) => void;
}): LinkTabItem[] => {
  const { t } = useTranslation();
  const maintenancePlanData = useSelector(getMaintenancePlan);
  const otsType = useSelector(getOtsType);
  const isMaintenancePlanError = maintenancePlanData === ERROR;
  const isBMM = isQueryBMM(result.vehicleKey);
  const technicalDocumentationUrl = useSelector(getTechnicalDocumentationLink);
  const versionCode = useSelector((state: RootState) => getLastSearchVehicleVersionCode(state));
  const technicalDocumentationVersionUrl =
    (versionCode && technicalDocumentationUrl?.replace('{vehicleId}', versionCode)) ?? '';
  const uparams = new URLSearchParams(location.search);
  uparams.set(TechnicalDataIdParam, FLUIDS_ID);
  const userRights = getData(useSelector(getUserRights));
  const isIam = result.catalogSource === 'IAM';

  useFetchOtsType();

  return buildCatalogTabItems({
    result,
    vehicleRouteChildren,
    setCatalogShowMenu,
    t,
    maintenancePlanData,
    isMaintenancePlanError,
    isBMM,
    technicalDocumentationVersionUrl,
    userRights,
    isIam,
    versionCode,
    otsType,
  });
};
